import { Component, EventEmitter, Input, Output, OnChanges, ChangeDetectionStrategy, input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { IPageSelection } from '../../../core/models/common/pagination.interface';

@Component({
  selector: 'app-custom-pagination',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './custom-pagination.component.html',
  styleUrl: './custom-pagination.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomPaginationComponent implements OnChanges {
  readonly pageSize = input<number>(10);
  readonly totalData = input<number>(0);

  @Output() readonly pageChange: EventEmitter<IPageSelection> = new EventEmitter();
  @Output() readonly pageSizeChange: EventEmitter<number> = new EventEmitter();

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() set resetCurrentPage(value: number) {
    this.currentPage = value;
    this.calculateTotalPages();
  }

  totalPages = 0;
  currentPage = 1;
  pageNumberArray: number[] = [];

  pagesPerGroup = 5;
  pageGroupStart = 1;

  constructor() {}

  ngOnChanges(): void {
    this.calculateTotalPages();
  }

  getMoreData(event: string): void {
    if (event === 'next' && this.currentPage < this.totalPages) {
      this.currentPage++;
    } else if (event === 'previous' && this.currentPage > 1) {
      this.currentPage--;
    }
    this.updatePageGroup();
    this.emitPageChange();
  }

  moveToPage(pageNumber: number): void {
    if (pageNumber > 0 && pageNumber <= this.totalPages) {
      this.currentPage = pageNumber;
      this.updatePageGroup();
      this.emitPageChange();
    }
  }

  changePageSize(pageSize: number): void {
    this.pageSize.apply(pageSize);
    this.pageSizeChange.emit(pageSize);
    this.currentPage = 1;
    this.calculateTotalPages();
  }

  private emitPageChange(): void {
    const skip = (this.currentPage - 1) * this.pageSize();
    const limit = this.pageSize();
    this.pageChange.emit({ skip, limit });
  }

  private calculateTotalPages(): void {
    this.totalPages = Math.ceil(this.totalData() / this.pageSize());
    this.updatePageGroup();
  }
  private updatePageGroup(): void {
    const start = Math.floor((this.currentPage - 1) / this.pagesPerGroup) * this.pagesPerGroup + 1;
    const end = Math.min(start + this.pagesPerGroup - 1, this.totalPages);
    this.pageGroupStart = start;
    this.pageNumberArray = Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  calculateMax(): number {
    const lastItem = Math.min(this.currentPage * this.pageSize(), this.totalData());
    return Math.min(lastItem, this.totalData());
  }

  calculateMin(): number {
    const firstItem = (this.currentPage - 1) * this.pageSize() + 1;
    return firstItem;
  }

  onSelectChange(event: Event): void {
    const value = parseInt((event.target as HTMLSelectElement).value, 10);
    const validValue = isNaN(value) ? 10 : value;
    this.changePageSize(validValue);
  }
}
